import React, { useState, useEffect } from "react";
import {
  getDatabase,
  ref,
  get,
  query,
  orderByChild,
  limitToLast,
  endAt,
} from "firebase/database";
import { app } from "./firebase/config";
import "./AdminPostManagement.css";

const AdminTransactionManagement = () => {
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [filter, setFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [transactionsPerPage] = useState(15);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [stats, setStats] = useState({
    totalTransactions: 0,
    totalAmount: 0,
    completedTransactions: 0,
    pendingTransactions: 0,
    revenueToday: 0,
    revenueThisMonth: 0,
    avgTransactionValue: 0,
  });

  const indexOfLastTransaction = currentPage * transactionsPerPage;
  const indexOfFirstTransaction = indexOfLastTransaction - transactionsPerPage;
  const currentTransactions = filteredTransactions.slice(
    indexOfFirstTransaction,
    indexOfLastTransaction
  );

  const totalPages = Math.ceil(
    filteredTransactions.length / transactionsPerPage
  );

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("vi-VN", {
      style: "currency",
      currency: "VND",
    }).format(amount);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    return date.toLocaleDateString("vi-VN", {
      hour: "2-digit",
      minute: "2-digit",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const fetchTransactions = async (forceRefresh = false, isLoadMore = false) => {
    setLoading(true);
    try {
      // Cache logic for initial load
      if (
        !forceRefresh &&
        !isLoadMore &&
        transactions.length === 0 &&
        localStorage.getItem("admin_transactions_cache")
      ) {
        try {
          const cachedData = JSON.parse(
            localStorage.getItem("admin_transactions_cache")
          );
          if (
            cachedData &&
            cachedData.transactions &&
            cachedData.transactions.length > 0
          ) {
            console.log("Sử dụng dữ liệu giao dịch từ cache");
            setTransactions(cachedData.transactions);
            setFilteredTransactions(cachedData.transactions);
            setLastUpdated(cachedData.timestamp);
            setLoading(false);
            return;
          }
        } catch (e) {
          console.error("Lỗi đọc cache:", e);
          localStorage.removeItem("admin_transactions_cache");
        }
      }

      const db = getDatabase(app);
      const transactionsRef = ref(db, "transactions");
      let transactionQuery;

      if (isLoadMore && transactions.length > 0) {
        const oldestTransaction = transactions[transactions.length - 1];
        const lastTimestamp = oldestTransaction.updatedAt || 0;
        // Fetch older transactions (updatedAt < lastTimestamp)
        transactionQuery = query(
          transactionsRef,
          orderByChild("updatedAt"),
          endAt(lastTimestamp - 1),
          limitToLast(50)
        );
      } else {
        // Initial load or refresh: fetch latest 50
        transactionQuery = query(
          transactionsRef,
          orderByChild("updatedAt"),
          limitToLast(50)
        );
      }

      const snapshot = await get(transactionQuery);

      if (snapshot.exists()) {
        const transactionsData = snapshot.val();
        const newTransactions = Object.entries(transactionsData)
          .map(([id, data]) => ({
            id,
            ...data,
          }))
          .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));

        if (isLoadMore) {
          setTransactions((prev) => [...prev, ...newTransactions]);
        } else {
          setTransactions(newTransactions);
          setFilteredTransactions(newTransactions);
          // Cache only the first page (latest transactions)
          const now = new Date().toISOString();
          localStorage.setItem(
            "admin_transactions_cache",
            JSON.stringify({ transactions: newTransactions, timestamp: now })
          );
          setLastUpdated(now);
        }

        if (newTransactions.length < 50) {
          setHasMore(false);
        } else {
          setHasMore(true);
        }

        // Recalculate stats (Note: this will only be accurate for loaded transactions)
        // Ideally stats should come from a separate aggregation node or API
        // For now, we calculate based on what we have, which might be partial.
        // To mitigate, we might want to fetch stats separately or keep the old "fetch all" for stats ONLY if needed.
        // But the goal is to reduce download. Let's accept partial stats or maybe fetch a stats node if it exists.
        // Assuming no stats node, we calculate based on loaded data.
        calculateStats(
          isLoadMore ? [...transactions, ...newTransactions] : newTransactions
        );
      } else {
        if (!isLoadMore) {
          setTransactions([]);
          setFilteredTransactions([]);
          setStats({
            totalTransactions: 0,
            totalAmount: 0,
            completedTransactions: 0,
            pendingTransactions: 0,
            revenueToday: 0,
            revenueThisMonth: 0,
            avgTransactionValue: 0,
          });
        }
        setHasMore(false);
      }
    } catch (error) {
      console.error("Lỗi khi tải dữ liệu giao dịch:", error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (transactionsArray) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);

    const completed = transactionsArray.filter(
      (t) =>
        t.status.toLowerCase() === "completed" ||
        t.status.toLowerCase() === "paid"
    );

    const todayTransactions = completed.filter(
      (t) => new Date(t.updatedAt) >= today
    );
    const monthTransactions = completed.filter(
      (t) => new Date(t.updatedAt) >= thisMonth
    );

    const totalAmount = completed.reduce(
      (sum, t) => sum + (Number(t.amount) || 0),
      0
    );

    setStats({
      totalTransactions: transactionsArray.length,
      totalAmount: totalAmount,
      completedTransactions: completed.length,
      pendingTransactions: transactionsArray.length - completed.length,
      revenueToday: todayTransactions.reduce(
        (sum, t) => sum + (Number(t.amount) || 0),
        0
      ),
      revenueThisMonth: monthTransactions.reduce(
        (sum, t) => sum + (Number(t.amount) || 0),
        0
      ),
      avgTransactionValue: completed.length ? totalAmount / completed.length : 0,
    });
  };

  useEffect(() => {
    fetchTransactions();
  }, []);

  const handleRefresh = () => {
    fetchTransactions(true);
  };

  const handleLoadMore = () => {
    fetchTransactions(false, true);
  };

  useEffect(() => {
    let results = [...transactions];

    if (filter !== "all") {
      results = results.filter((transaction) => {
        const status = transaction.status.toLowerCase();
        if (filter === "completed") {
          return status === "completed" || status === "paid";
        } else if (filter === "pending") {
          return status !== "completed" && status !== "paid";
        }
        return true;
      });
    }

    if (searchText.trim() !== "") {
      const searchLower = searchText.toLowerCase();
      results = results.filter((transaction) => {
        return (
          (transaction.id &&
            transaction.id.toLowerCase().includes(searchLower)) ||
          (transaction.userId &&
            transaction.userId.toLowerCase().includes(searchLower)) ||
          (transaction.itemName &&
            transaction.itemName.toLowerCase().includes(searchLower)) ||
          (transaction.paymentInfo?.reference &&
            transaction.paymentInfo.reference
              .toLowerCase()
              .includes(searchLower))
        );
      });
    }

    if (fromDate || toDate) {
      results = results.filter((transaction) => {
        if (!transaction.updatedAt) return false;

        const transactionDate = new Date(transaction.updatedAt);
        transactionDate.setHours(transactionDate.getHours() + 7);

        if (fromDate && toDate) {
          const from = new Date(fromDate);
          const to = new Date(toDate);
          to.setHours(23, 59, 59, 999);
          return transactionDate >= from && transactionDate <= to;
        } else if (fromDate) {
          const from = new Date(fromDate);
          return transactionDate >= from;
        } else if (toDate) {
          const to = new Date(toDate);
          to.setHours(23, 59, 59, 999);
          return transactionDate <= to;
        }
        return true;
      });
    }

    setFilteredTransactions(results);
    setCurrentPage(1);
  }, [searchText, filter, transactions, fromDate, toDate]);

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-semibold">Quản lý giao dịch</h2>
        <div className="flex items-center gap-4">
          {lastUpdated && (
            <span className="text-sm text-gray-500">
              Cập nhật: {new Date(lastUpdated).toLocaleTimeString("vi-VN")}
            </span>
          )}
          <button
            onClick={handleRefresh}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            disabled={loading}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className={`h-5 w-5 mr-2 ${loading ? "animate-spin" : ""}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            {loading ? "Đang tải..." : "Làm mới"}
          </button>
        </div>
      </div>
      <div className="mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg shadow-md">
            <h3 className="text-sm font-medium text-gray-500">
              Tổng giao dịch
            </h3>
            <p className="text-2xl font-bold text-gray-900 mt-2">
              {stats.totalTransactions}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-md">
            <h3 className="text-sm font-medium text-gray-500">Doanh thu</h3>
            <p className="text-2xl font-bold text-gray-900 mt-2">
              {formatCurrency(stats.totalAmount)}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-md">
            <h3 className="text-sm font-medium text-gray-500">Hoàn thành</h3>
            <p className="text-2xl font-bold text-gray-900 mt-2">
              {stats.completedTransactions}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-md">
            <h3 className="text-sm font-medium text-gray-500">Đang xử lý</h3>
            <p className="text-2xl font-bold text-gray-900 mt-2">
              {stats.pendingTransactions}
            </p>
          </div>
        </div>
      </div>

      <div className="mb-6">
        <div className="flex flex-wrap gap-4">
          <input
            type="text"
            placeholder="Tìm kiếm theo ID, người dùng, trạng thái..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="px-4 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
          />
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="px-4 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
          />
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="px-4 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
          />
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-4 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">Tất cả</option>
            <option value="completed">Hoàn thành</option>
            <option value="pending">Đang xử lý</option>
          </select>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full bg-white">
          <thead>
            <tr className="bg-gray-100 text-gray-600 uppercase text-sm leading-normal">
              <th className="py-3 px-6 text-left">ID</th>
              <th className="py-3 px-6 text-left">Người dùng</th>
              <th className="py-3 px-6 text-left">Tên gói</th>
              <th className="py-3 px-6 text-left">Số tiền</th>
              <th className="py-3 px-6 text-left">Trạng thái</th>
              <th className="py-3 px-6 text-left">Ngày</th>
            </tr>
          </thead>
          <tbody className="text-gray-600 text-sm">
            {currentTransactions.length > 0 ? (
              currentTransactions.map((transaction) => (
                <tr
                  key={transaction.id}
                  className="border-b border-gray-200 hover:bg-gray-50"
                >
                  <td className="py-3 px-6 text-left">{transaction.id}</td>
                  <td className="py-3 px-6 text-left">
                    {transaction.userId || "N/A"}
                  </td>
                  <td className="py-3 px-6 text-left">
                    {transaction.itemName || "N/A"}
                  </td>
                  <td className="py-3 px-6 text-left">
                    {formatCurrency(transaction.amount)}
                  </td>
                  <td className="py-3 px-6 text-left">
                    {transaction.status || "N/A"}
                  </td>
                  <td className="py-3 px-6 text-left">
                    {formatDate(transaction.updatedAt)}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="py-6 text-center text-gray-500">
                  Không tìm thấy giao dịch nào
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {hasMore && (
        <div className="flex justify-center mt-6">
          <button
            onClick={handleLoadMore}
            disabled={loading}
            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors flex items-center"
          >
            {loading ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-700"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Đang tải thêm...
              </>
            ) : (
              "Tải thêm giao dịch cũ hơn"
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminTransactionManagement;
